import { useCallback, useEffect, useRef, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { GET_SERIES_CLASSIFICATION } from 'modules/Apollo/queries';
import { useClinicalStepWriters } from '../clinicalStepStore';
import { useBaseViewerData } from '../../Viewer/StudyLoader/useStudies';
import type { GetSeriesClassificationQuery } from 'generated/graphql';
import { flattenSeriesClassification } from '../../Viewer/StudiesBar/HangingProtocols/HPv4/hangingProtocolFormHelpers';

type ClassifyRequest = {
  viewportId: string;
  stepIndex: number;
  seriesDescription: string;
  seriesSmid: Nullable<string>;
  studyDescription?: string;
};

type PendingClassification = {
  stepIndex: number;
  abort: AbortController;
};

export function useClinicalViewportClassification() {
  // `pendingClassificationsCount` is a React state counter so components re-render when
  // the in-flight count changes. `pendingRef` is a ref (not state) because the Map must
  // be mutated synchronously in the same microtask as the fetch, and making it state
  // would cause a stale-closure read of the old Map on rapid consecutive classify() calls.
  const [pendingClassificationsCount, setPendingClassificationsCount] = useState(0);
  const pendingRef = useRef<Map<string, PendingClassification>>(new Map());
  const { applyServerCriteriaToViewport } = useClinicalStepWriters();

  const [fetchClassification] =
    useLazyQuery<GetSeriesClassificationQuery>(GET_SERIES_CLASSIFICATION);

  // Series classifications are already part of the same payload the viewer used to render this
  // case (`GetBaseViewerData` -> studies -> seriesList -> classifications), so reading them here is
  // a Map lookup rather than a second round trip for data already sitting in the Apollo cache.
  // `classificationBySeriesSmid` is memoized once in `useStudies.ts` -- `SeriesClassifierInput`
  // reads the same map rather than each caller building its own (VX-4970 review).
  const { classificationBySeriesSmid } = useBaseViewerData({
    context: { telemetry: { component: 'useClinicalViewportClassification' } },
  });

  const classify = useCallback(
    (request: ClassifyRequest) => {
      const { viewportId, stepIndex, seriesDescription, seriesSmid, studyDescription } = request;
      // Without a smid, applyServerCriteriaToViewport can never find a match to write into (see
      // holdsSeries in clinicalStepStore.ts), so the fetch below would be wasted and would leave
      // pendingClassifications stuck incremented until it resolves into a no-op.
      if (seriesSmid == null) return;
      const key = `${stepIndex}:${viewportId}`;

      // Cancel existing classification for this viewport
      const existing = pendingRef.current.get(key);
      if (existing) {
        existing.abort.abort();
        pendingRef.current.delete(key);
        setPendingClassificationsCount((n) => Math.max(0, n - 1));
      }

      const abortController = new AbortController();
      pendingRef.current.set(key, { stepIndex, abort: abortController });
      setPendingClassificationsCount((n) => n + 1);

      const finishPending = () => {
        // Skip cleanup if cancelled or superseded — the replacement manages its own counter.
        if (!abortController.signal.aborted) {
          pendingRef.current.delete(key);
          setPendingClassificationsCount((n) => Math.max(0, n - 1));
        }
      };

      void (async () => {
        try {
          // The series' own classification, not the classification of its description. The text
          // yields only what its words say -- e.g. a description like "ABD & PEL" resolves to an
          // anatomic focus and a field of view and stops there -- while the series record adds the
          // view orientation and kernel that separate it from its study's localizer, which shares
          // every category the text has. Saving the thinner rule lets the backend hand this
          // viewport the localizer instead (VX-4970). Falls back to the text classifier when the
          // series has no classification stored.
          const seriesRecordCriteria = classificationBySeriesSmid?.get(seriesSmid)?.seriesCriteria;
          const classification =
            flattenSeriesClassification(seriesRecordCriteria).length > 0
              ? (seriesRecordCriteria as unknown)
              : (
                  await fetchClassification({
                    variables: {
                      dicomSeriesDescription: seriesDescription,
                      dicomStudyDescription: studyDescription ?? undefined,
                    },
                    context: {
                      telemetry: { component: 'useClinicalViewportClassification' },
                      fetchOptions: { signal: abortController.signal },
                    },
                  })
                ).data?.seriesClassification?.classification;

          if (abortController.signal.aborted || !classification) return;

          const criteria = flattenSeriesClassification(classification).map((c) => ({
            category: c.category,
            value: c.value,
          }));

          // Both atoms, one operation. The classification is server-derived, so it must never be
          // what makes a step dirty -- and each atom is written only if it independently still
          // holds the series this fetch was issued for. See `applyServerCriteriaToViewport` for
          // what each of those two guards is protecting against; both were paid for in bugs.
          applyServerCriteriaToViewport({ stepIndex, viewportId, criteria, seriesSmid });
        } catch {
          // Classification failed — viewport saves without criteria
        } finally {
          finishPending();
        }
      })();
    },
    [classificationBySeriesSmid, fetchClassification, applyServerCriteriaToViewport]
  );

  const cancelForStep = useCallback((stepIndex: number) => {
    for (const [key, pending] of pendingRef.current) {
      if (pending.stepIndex === stepIndex) {
        pending.abort.abort();
        pendingRef.current.delete(key);
        setPendingClassificationsCount((n) => Math.max(0, n - 1));
      }
    }
  }, []);

  const cancelAll = useCallback(() => {
    for (const [, pending] of pendingRef.current) {
      pending.abort.abort();
    }
    pendingRef.current.clear();
    setPendingClassificationsCount(0);
  }, []);

  // Cancel all in-flight requests on unmount to prevent stale Recoil writes
  // after the component has been removed from the tree.
  useEffect(() => () => cancelAll(), [cancelAll]);

  return {
    classify,
    pendingClassifications: pendingClassificationsCount,
    cancelForStep,
    cancelAll,
  };
}
